import React, { useState, useEffect, useCallback } from 'react';
import { helpAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { LifeBuoy, Search, FileText, Download, Plus, Pencil, Trash2, Loader2, ChevronDown, BarChart3, Sparkles, MessageCircle, Wand2, CheckCircle2, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  all: 'All',
  getting_started: 'Getting Started',
  login_account: 'Login & Account',
  projects: 'Projects',
  tasks: 'Tasks',
  clients: 'Clients',
  finance: 'Finance',
  proposals: 'Proposals',
  ffe_procurement: 'FF&E & Procurement',
  vendors: 'Vendors',
  crm: 'CRM',
  calendar: 'Calendar',
  reports: 'Reports',
  settings: 'Settings',
  other: 'Other',
};

function FaqItem({ faq, canEdit, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const handleToggle = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) helpAPI.viewFaq(faq.faq_id).catch(() => {});
  };
  return (
    <Collapsible open={open} onOpenChange={handleToggle} className="border rounded-lg bg-card">
      <div className="flex items-center">
        <CollapsibleTrigger className="flex-1 flex items-center justify-between p-4 text-left hover:bg-muted/30 transition" data-testid={`faq-${faq.faq_id}`}>
          <div>
            <div className="font-medium text-sm">{faq.question}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{CATEGORY_LABELS[faq.category] || faq.category}</div>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        {canEdit && (
          <div className="flex gap-1 pr-2">
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(faq); }} data-testid={`edit-faq-${faq.faq_id}`}><Pencil className="h-3 w-3" /></Button>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(faq); }} className="text-red-600"><Trash2 className="h-3 w-3" /></Button>
          </div>
        )}
      </div>
      <CollapsibleContent>
        <div className="px-4 pb-4 text-sm text-muted-foreground whitespace-pre-wrap">{faq.answer}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FAQManagerDialog({ open, onClose, editing, onSaved }) {
  const empty = { question: '', answer: '', category: 'getting_started', tags: '', order: 0, roles: ['admin', 'staff', 'client'] };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...empty, ...editing, tags: (editing.tags || []).join(', ') });
    else setForm(empty);
  }, [editing, open]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) { toast.error('Question and answer required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags };
      if (editing) await helpAPI.updateFaq(editing.faq_id, payload);
      else await helpAPI.createFaq(payload);
      toast.success(editing ? 'FAQ updated' : 'FAQ added');
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle><DialogDescription>Curate answers your team and clients can search</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Question *</Label><Input value={form.question} onChange={e => setForm({...form, question: e.target.value})} required data-testid="faq-question-input" /></div>
          <div><Label>Answer * (supports markdown-like text)</Label><Textarea rows={6} value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} required data-testid="faq-answer-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="faq-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).filter(([k]) => k !== 'all').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Display order</Label><Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} /></div>
          </div>
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-faq-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GuidesSection({ role }) {
  const [guides, setGuides] = useState({});
  useEffect(() => { helpAPI.listGuides().then(r => setGuides(r.data)).catch(() => {}); }, []);
  const myRole = role === 'admin' ? 'admin' : role === 'staff' ? 'staff' : 'client';
  const visible = role === 'admin' ? Object.keys(guides) : [myRole];

  const downloadGuide = async (r) => {
    try {
      const res = await helpAPI.getGuide(r);
      const blob = new Blob([res.data.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${r}_quickstart.md`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed'); }
  };

  const [viewing, setViewing] = useState(null);
  const openGuide = async (r) => {
    try { const res = await helpAPI.getGuide(r); setViewing(res.data); }
    catch (e) { toast.error('Failed'); }
  };

  if (Object.keys(guides).length === 0) return <p className="text-xs text-muted-foreground">Loading guides...</p>;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="guides-grid">
        {visible.map(r => guides[r] && (
          <Card key={r} className="hover:shadow-md transition cursor-pointer" onClick={() => openGuide(r)} data-testid={`guide-card-${r}`}>
            <CardContent className="p-5">
              <FileText className="h-6 w-6 text-violet-500 mb-2" />
              <div className="font-medium">{guides[r].title}</div>
              <div className="text-xs text-muted-foreground mt-1">{guides[r].subtitle}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openGuide(r); }}>Read</Button>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadGuide(r); }}><Download className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {viewing && <>
            <DialogHeader><DialogTitle>{viewing.title}</DialogTitle><DialogDescription>{viewing.subtitle}</DialogDescription></DialogHeader>
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed" data-testid="guide-content">{viewing.content}</pre>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SuggestedFaqsSection({ onPublished }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await helpAPI.listSuggestions(); setSuggestions(r.data); }
    catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await helpAPI.generateSuggestions({ days: 14 });
      if (r.data.generated > 0) toast.success(`${r.data.generated} new suggestions`);
      else toast.info(r.data.message || 'No new user activity');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const approveQuick = async (s) => {
    try {
      await helpAPI.approveSuggestion(s.suggestion_id, {});
      toast.success('Published as FAQ');
      load(); onPublished?.();
    } catch (e) { toast.error('Failed'); }
  };

  const dismiss = async (s) => {
    try { await helpAPI.dismissSuggestion(s.suggestion_id); toast.success('Dismissed'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const openEdit = (s) => { setEditing({ ...s }); setEditOpen(true); };

  const saveEdited = async () => {
    try {
      await helpAPI.approveSuggestion(editing.suggestion_id, {
        question: editing.question, answer: editing.answer, category: editing.category, tags: editing.tags,
      });
      toast.success('Published');
      setEditOpen(false); load(); onPublished?.();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <Card data-testid="suggested-faqs-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-medium flex items-center gap-2"><Wand2 className="h-4 w-4 text-violet-500" />AI-Suggested FAQs</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">Turns questions your users actually ask into ready-to-publish FAQs. Approve with one click, edit inline, or dismiss.</p>
          </div>
          <Button onClick={generate} disabled={generating} size="sm" data-testid="generate-suggestions-btn">
            {generating ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-2" />}
            Generate from last 14 days
          </Button>
        </div>

        {loading ? <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div> :
          suggestions.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No pending suggestions. Click "Generate" to analyse recent user activity.</p> : (
            <div className="space-y-2" data-testid="suggestions-list">
              {suggestions.map(s => (
                <div key={s.suggestion_id} className="p-3 border rounded-lg bg-muted/20" data-testid={`suggestion-${s.suggestion_id}`}>
                  <div className="flex items-start gap-3 justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{CATEGORY_LABELS[s.category] || s.category}</span>
                        {(s.tags || []).map((t, i) => <span key={i} className="inline-block px-2 py-0.5 rounded-full bg-muted">{t}</span>)}
                      </div>
                      <div className="font-medium text-sm">{s.question}</div>
                      <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{s.answer}</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" onClick={() => approveQuick(s)} data-testid={`approve-${s.suggestion_id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Publish</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => dismiss(s)} className="text-red-600"><XIcon className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </CardContent>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit &amp; Publish FAQ</DialogTitle><DialogDescription>Refine the suggestion before making it live</DialogDescription></DialogHeader>
          {editing && <div className="space-y-3">
            <div><Label>Question</Label><Input value={editing.question} onChange={e => setEditing({...editing, question: e.target.value})} data-testid="edit-suggestion-question" /></div>
            <div><Label>Answer</Label><Textarea rows={6} value={editing.answer} onChange={e => setEditing({...editing, answer: e.target.value})} data-testid="edit-suggestion-answer" /></div>
            <div><Label>Category</Label>
              <Select value={editing.category} onValueChange={v => setEditing({...editing, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).filter(([k]) => k !== 'all').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdited} data-testid="publish-edited-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AnalyticsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    helpAPI.analytics(30).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading analytics...</div>;
  if (!data) return <p className="text-muted-foreground">No data</p>;
  return (
    <div className="space-y-4" data-testid="helper-analytics">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-muted-foreground text-xs">Chat messages (30d)</div><div className="text-2xl font-light mt-1">{data.total_chat_messages}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-muted-foreground text-xs">FAQ searches (30d)</div><div className="text-2xl font-light mt-1">{data.total_searches}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-muted-foreground text-xs">Top queries</div><div className="text-2xl font-light mt-1">{data.top_searches.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-muted-foreground text-xs">FAQs viewed</div><div className="text-2xl font-light mt-1">{data.top_faqs.reduce((s, f) => s + (f.view_count || 0), 0)}</div></CardContent></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-5">
          <div className="font-medium text-sm mb-3">Top AI Helper questions</div>
          {data.top_questions.length === 0 ? <p className="text-xs text-muted-foreground">No chat activity yet</p> : (
            <div className="space-y-2">{data.top_questions.map((q, i) => (
              <div key={i} className="flex justify-between text-xs border-b pb-1"><span className="truncate flex-1">{q.question}</span><span className="font-medium ml-2">{q.count}</span></div>
            ))}</div>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="font-medium text-sm mb-3">Top searched FAQs</div>
          {data.top_faqs.length === 0 ? <p className="text-xs text-muted-foreground">No views yet</p> : (
            <div className="space-y-2">{data.top_faqs.map((f, i) => (
              <div key={i} className="flex justify-between text-xs border-b pb-1"><span className="truncate flex-1">{f.question}</span><span className="font-medium ml-2">{f.view_count || 0}</span></div>
            ))}</div>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}

export default function HelpSupport() {
  const { user, isAdmin } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [editing, setEditing] = useState(null);
  const [faqDialog, setFaqDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (category !== 'all') params.category = category;
      if (search.trim()) params.q = search.trim();
      const r = await helpAPI.listFaqs(params);
      setFaqs(r.data);
    } catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => { load(); }, [category]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete "${faq.question}"?`)) return;
    try { await helpAPI.deleteFaq(faq.faq_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="help-support-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
              <LifeBuoy className="h-7 w-7 text-violet-500" />Help &amp; Support
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Find answers fast — search FAQs, read quick-start guides, or ask the AI helper</p>
          </div>
          {isAdmin && <Button onClick={() => { setEditing(null); setFaqDialog(true); }} data-testid="add-faq-btn"><Plus className="h-4 w-4 mr-2" />Add FAQ</Button>}
        </div>

        <Card className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-100">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-medium">Ask The Eleventh Helper</div>
              <p className="text-sm text-muted-foreground mt-1">Click the <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs"><MessageCircle className="h-3 w-3" />Ask The Eleventh</span> button at the bottom-right of any page to get instant answers about any feature or workflow.</p>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="faqs">
          <TabsList>
            <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs &amp; Guides</TabsTrigger>
            {isAdmin && <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-3.5 w-3.5 mr-1" />Helper Analytics</TabsTrigger>}
          </TabsList>

          <TabsContent value="faqs" className="space-y-6 mt-4">
            {/* Guides */}
            <div>
              <div className="text-sm uppercase tracking-wider text-muted-foreground mb-3">Downloadable Quick-Start Guides</div>
              <GuidesSection role={user?.role} />
            </div>

            {/* FAQs */}
            <div>
              <div className="text-sm uppercase tracking-wider text-muted-foreground mb-3">Frequently Asked Questions</div>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search FAQs (e.g. 'create project')" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="faq-search" />
                </div>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="sm:w-56" data-testid="faq-category-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {loading ? <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted/40 rounded-lg" />)}</div> :
                faqs.length === 0 ? <Card><CardContent className="py-10 text-center text-muted-foreground">No FAQs match your search. Try asking the AI Helper instead!</CardContent></Card> :
                  <div className="space-y-2" data-testid="faq-list">
                    {faqs.map(f => <FaqItem key={f.faq_id} faq={f} canEdit={isAdmin} onEdit={(faq) => { setEditing(faq); setFaqDialog(true); }} onDelete={handleDelete} />)}
                  </div>
              }
            </div>
          </TabsContent>

          {isAdmin && <TabsContent value="analytics" className="mt-4 space-y-6"><SuggestedFaqsSection onPublished={load} /><AnalyticsSection /></TabsContent>}
        </Tabs>
      </div>

      <FAQManagerDialog open={faqDialog} onClose={() => setFaqDialog(false)} editing={editing} onSaved={load} />
    </Layout>
  );
}
